import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCodes } from "http-status-codes";
import { useErrorHandler } from "../../custom-hooks/useErrorHandler";
import { useAuth } from "../../context/AuthProvider";
import useCustomFormik from "../../custom-hooks/useCustomFormik";
import { userLoginFields, UserLoginSchemaFactory } from "../../validation-schemas/UserLoginSchema";
import CustomInput from "../../custom-components/form-inputs/CustomInput";
import TogglePassword from "../shared/form-inputs/TogglePassword";
import CustomError from "../../custom-components/form-inputs/CustomError";
import LangSelect from "../header/LangSelect";
import HomeButtonSVG from "../shared/svg/HomeButtonSVG";
import GoogleLoginButton from "./GoogleLoginButton";
import { User } from "../../domain/models/User";

export default function LoginPage() {
    const [isPasswordVisible, setPasswordVisibility] = useState(false);
    const { errorKey, setErrorKey, resetErrors, isError } = useErrorHandler(StatusCodes.UNAUTHORIZED);
    const { signIn } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleNavigation = (user: User | undefined, resetForm?: (nextState?: Partial<any>) => void) => {
        if (!isError() && user) {
            resetForm && resetForm();
            navigate("/");
        }
    };

    const { form } = useCustomFormik({
        initial: null,
        fields: userLoginFields,
        schema: UserLoginSchemaFactory(t),
        onSubmit: async (values, actions) => {
            actions.setSubmitting(true);
            resetErrors();

            if (!values.email || !values.password) {
                return;
            }

            try {
                const result = await signIn(values.email, values.password);
                handleNavigation(result, actions.resetForm);
            } catch (err) {
                setErrorKey("errors.common");
                return;
            }
        },
    });

    return (
        <div className="flex items-center justify-center h-[100vh]">
            <div className="max-w-sm bg-gray-200 px-[24px] py-[24px] rounded-xl">
                <div className="flex items-center justify-between w-[400px]">
                    <Link to={"/"}>
                        <HomeButtonSVG fill="#000" />
                    </Link>
                    <LangSelect
                        additionalButtonClassName="absolute z-10 right-[65px]"
                        additionalPopupClassName="absolute z-10 right-[65px] top-[10px] bg-gray-100 shadow-md"
                    />
                </div>
                <form id="loginForm" onSubmit={form.handleSubmit}>
                    <h1 className="block text-center mt-4 mb-4 text-[22px] font-normal text-gray-700">
                        {t("auth.login.title")}
                    </h1>

                    <CustomInput
                        type="email"
                        labelKey="auth.fields.email"
                        name="email"
                        placeholderKey="auth.fields.email"
                        errorKey={form.errors.email}
                        value={form.values.email}
                        onChange={form.handleChange}
                        className={`${form.touched.email && form.errors.email && "bg-red-100"}`}
                    />

                    <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                            <CustomInput
                                type={isPasswordVisible ? "text" : "password"}
                                labelKey="auth.fields.password"
                                name="password"
                                placeholderKey="auth.fields.password"
                                errorKey={form.errors.password}
                                value={form.values.password}
                                onChange={form.handleChange}
                                className={`${form.touched.password && form.errors.password && "bg-red-100"}`}
                            />
                        </div>
                        <TogglePassword
                            isPasswordVisible={isPasswordVisible}
                            setPasswordVisible={setPasswordVisibility}
                        />
                    </div>

                    <button
                        className="flex items-center text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center h-[42px] mt-2"
                        type="submit"
                    >
                        {t("auth.actions.login")}
                    </button>

                    <CustomError errorKey={errorKey} name={errorKey} />
                </form>
                <GoogleLoginButton onSuccess={(curentUser) => handleNavigation(curentUser)} />
            </div>
        </div>
    );
}
